<script setup>
// Fork component (RBAC v2 — H1): renders the custom-role permissions grouped
// by category instead of upstream's flat list. The three fork visibility
// permissions are mutually exclusive (a role sees all OR team OR assigned).
// Unknown permissions land in the OTHER group so future additions show up
// without editing this file.
import { computed } from 'vue';
// Import the fork's permissions.custom.js explicitly: this component is a
// `.custom.vue`, and the Vite customOverridePlugin deliberately does NOT rewrite
// imports coming from `.custom.` files, so importing the pristine permissions.js
// would drop every fork permission from the editor (whole RBAC v2 UI unusable).
import { AVAILABLE_CUSTOM_ROLE_PERMISSIONS } from 'dashboard/constants/permissions.custom.js';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const VISIBILITY_PERMISSIONS = [
  'manage_all_conversations',
  'manage_team_and_assigned_conversations',
  'manage_assigned_conversations_only',
];

const GROUP_DEFINITIONS = [
  { key: 'VISIBILITY', permissions: VISIBILITY_PERMISSIONS },
  {
    key: 'CONVERSATIONS',
    permissions: [
      'conversation_manage',
      'conversation_unassigned_manage',
      'conversation_participating_manage',
    ],
  },
  { key: 'PEOPLE', permissions: ['contact_manage', 'agent_manage'] },
  { key: 'REPORTS', permissions: ['report_manage', 'report_view'] },
  {
    key: 'SETTINGS',
    permissions: [
      'knowledge_base_manage',
      'agent_availability_manage',
      'label_manage',
    ],
  },
];

const groups = computed(() => {
  const available = AVAILABLE_CUSTOM_ROLE_PERMISSIONS;
  const bucketed = GROUP_DEFINITIONS.map(group => ({
    key: group.key,
    permissions: group.permissions.filter(p => available.includes(p)),
  }));
  const known = GROUP_DEFINITIONS.flatMap(group => group.permissions);
  const leftover = available.filter(p => !known.includes(p));
  if (leftover.length) {
    bucketed.push({ key: 'OTHER', permissions: leftover });
  }
  return bucketed.filter(group => group.permissions.length);
});

const isSelected = permission => props.modelValue.includes(permission);

const togglePermission = permission => {
  let next;
  if (isSelected(permission)) {
    next = props.modelValue.filter(p => p !== permission);
  } else if (VISIBILITY_PERMISSIONS.includes(permission)) {
    // Visibility permissions are mutually exclusive: picking one drops the rest.
    next = [
      ...props.modelValue.filter(p => !VISIBILITY_PERMISSIONS.includes(p)),
      permission,
    ];
  } else {
    next = [...props.modelValue, permission];
  }
  emit('update:modelValue', next);
};
</script>

<template>
  <div class="flex flex-col gap-4 mb-4 mt-2">
    <fieldset v-for="group in groups" :key="group.key" class="border-0 p-0 m-0">
      <legend class="text-sm font-medium text-n-slate-12 pb-1.5">
        {{ $t(`CUSTOM_ROLE.GROUPS.${group.key}`) }}
      </legend>
      <div class="flex flex-col gap-2.5">
        <div
          v-for="permission in group.permissions"
          :key="permission"
          class="flex items-center"
        >
          <input
            :id="permission"
            type="checkbox"
            :checked="isSelected(permission)"
            :value="permission"
            name="permissions"
            class="ltr:mr-2 rtl:ml-2"
            @change="togglePermission(permission)"
          />
          <label :for="permission" class="text-sm font-normal">
            {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
          </label>
        </div>
      </div>
    </fieldset>
  </div>
</template>
